// chakra imports
import { Box, ChakraProvider, Portal, Text } from '@chakra-ui/react';
import Footer from '../components/Footer/Footer';
// core components
import AuthNavbar from '../components/Navbars/AuthNavbar';
import React, { useEffect, useRef } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import routes from '../routes';
import '@fontsource/urbanist/300.css';
import '@fontsource/urbanist/400.css';
import '@fontsource/urbanist/500.css';
import '@fontsource/urbanist/600.css';
import '@fontsource/urbanist/700.css';
import theme from '../theme/theme';
import { RouteConfigArray } from '../types/routes';
import { DOCUMENT_TITLE_BRAND } from '../utils/documentTitle';

interface PagesProps {
  [key: string]: any;
}

export default function Pages(props: PagesProps) {
	const { ...rest } = props;
	// ref for the wrapper div
	const wrapper = useRef<HTMLDivElement>(null);
	const location = useLocation();
	
	// Check if we're on the sign-in or sign-up page
	const isSignInPage =
		location.pathname === "/auth/signin" || location.pathname === "/auth/signup";
	
	useEffect(() => {
		document.body.style.overflow = 'unset';
		// Background image is now handled in the SignIn/SignUp components
		// Clean up any existing background images
		document.documentElement.style.backgroundImage = "";
		document.documentElement.style.backgroundSize = "";
		document.documentElement.style.backgroundPosition = "";
		document.documentElement.style.backgroundRepeat = "";
		document.documentElement.style.backgroundAttachment = "";
		document.documentElement.style.minHeight = "";
		document.body.style.backgroundImage = "";
		document.body.style.backgroundSize = "";
		document.body.style.backgroundPosition = "";
		document.body.style.backgroundRepeat = "";
		document.body.style.backgroundAttachment = "";
		document.body.style.minHeight = "";
	}, [isSignInPage]);

	useEffect(() => {
		if (location.pathname === "/auth/signin") {
			document.title = `Sign In | ${DOCUMENT_TITLE_BRAND}`;
		} else if (location.pathname === "/auth/signup") {
			document.title = `Sign Up | ${DOCUMENT_TITLE_BRAND}`;
		} else {
			document.title = DOCUMENT_TITLE_BRAND;
		}
	}, [location.pathname]);
	
	const getActiveRoute = (routes: RouteConfigArray): string => {
		if (!routes || !Array.isArray(routes) || routes.length === 0) {
			return 'Default Brand Text';
		}
		let activeRoute = 'Default Brand Text';
		for (let i = 0; i < routes.length; i++) {
			const route = routes[i];
			if (route.collapse && route.views) {
				const collapseActiveRoute = getActiveRoute(route.views);
				if (collapseActiveRoute !== activeRoute) {
					return collapseActiveRoute;
				}
			} else if (route.category && route.views) {
				const categoryActiveRoute = getActiveRoute(route.views);
				if (categoryActiveRoute !== activeRoute) {
					return categoryActiveRoute;
				}
			} else {
				if (route.layout && route.path && window.location.href.indexOf(route.layout + route.path) !== -1) {
					return route.name;
				}
			}
		}
		return activeRoute;
	};
	
	const getActiveNavbar = (routes: RouteConfigArray): boolean => {
		if (!routes || !Array.isArray(routes) || routes.length === 0) {
			return false;
		}
		let activeNavbar = false;
		for (let i = 0; i < routes.length; i++) {
			const route = routes[i];
			if (route.category && route.views) {
				const categoryActiveNavbar = getActiveNavbar(route.views);
				if (categoryActiveNavbar !== activeNavbar) {
					return categoryActiveNavbar;
				}
			} else {
				if (route.layout && route.path && window.location.href.indexOf(route.layout + route.path) !== -1) {
					if (route.secondaryNavbar) {
						return route.secondaryNavbar;
					}
				}
			}
		}
		return activeNavbar;
	};
	
	const getRoutes = (routes: RouteConfigArray): JSX.Element[] => {
		if (!routes || !Array.isArray(routes) || routes.length === 0) {
			return [];
		}
		const routeList: JSX.Element[] = [];
		routes.forEach((prop, key) => {
			if (prop.collapse && prop.views) {
				routeList.push(...getRoutes(prop.views));
			} else if (prop.category === 'account' && prop.views) {
				routeList.push(...getRoutes(prop.views));
			} else if (prop.layout === '/auth' && prop.path && prop.component) {
				// Use exact match to ensure proper routing
				const routePath = prop.layout + prop.path;
				routeList.push(
					<Route 
						exact 
						path={routePath} 
						component={prop.component} 
						key={`auth-route-${key}`}
					/>
				);
			}
		});
		return routeList;
	};
	
	const navRef = useRef<HTMLDivElement>(null);
	document.documentElement.dir = 'ltr';
	
	return (
		<ChakraProvider theme={theme as any} resetCss={false} w='100%' {...({} as any)}>
			<div
				ref={navRef}
				className="relative z-[1] min-h-svh w-full bg-muted font-[family-name:var(--font-primary)] dark:bg-content-canvas"
				data-app-route-surface
				data-app-auth-surface
			>
				{/* Only show navbar on non-sign-in pages for consistent sign-in experience */}
				{!isSignInPage && (
					<Portal containerRef={navRef}>
						<AuthNavbar secondary={getActiveNavbar(routes as RouteConfigArray)} logoText='PURITY UI DASHBOARD' />
					</Portal>
				)}
				<div className="w-full">
					<div ref={wrapper} className="w-full">
						<Switch>
							{getRoutes(routes as RouteConfigArray)}
							{/* Fallback: redirect /auth to /auth/signin */}
							<Route exact path="/auth">
								{() => {
									window.location.assign("/auth/signin");
									return null;
								}}
							</Route>
							{/* Show error if no route matched */}
							<Route path="/auth">
								{() => {
									console.error('AuthLayout: No route matched for:', location.pathname);
									return (
										<Box p={4} bg="red" color="white">
											<h2>ERROR: Auth route not found</h2>
											<p>Pathname: {location.pathname}</p>
											<p>Routes created: {getRoutes(routes as RouteConfigArray).length}</p>
										</Box>
									);
								}}
							</Route>
						</Switch>
					</div>
				</div>
				{!isSignInPage && (
					<Box px='24px' mx='auto' width='1044px' maxW='100%'>
						<Footer />
					</Box>
				)}
			</div>
		</ChakraProvider>
	);
}

